import { createRoot } from "react-dom/client";
import WrappedAIChatWidget from "./WrappedAIChatWidget";
import { AIChatWidgetProps } from "./AIChatWidget";
import "./AIChatWidget.css";
import React from "react";
import ReactDOM from "react-dom";
import { createShadowRoot, isShadowDomSupported } from "../../lib/utils/shadowDomUtils";

// Ensure that React and ReactDOM are assigned to the global window object.
if (typeof window !== "undefined") {
  window.React = React;
  window.ReactDOM = ReactDOM;
}

/**
 * Extended options interface for widget initialization
 * Includes all props from AIChatWidget plus additional configuration options
 */
interface AIChatWidgetOptions {
  enabled?: boolean;
  botName?: string;
  botLogo?: string | null;
  primaryColor?: string;
  secondaryColor?: string;
  position?: "left" | "right";
  initialMessages?: string[];
  apiBaseUrl?: string; // Optional explicit API base URL override
}

interface AIChatWidgetOptionsWithAppIdAndToken extends AIChatWidgetOptions {
  appId: string;
  token: string;
}

// Add new interface for the global config with autoInit flag
interface AIChatWidgetConfig extends Partial<AIChatWidgetOptionsWithAppIdAndToken> {
  autoInit?: boolean;
}

const defaultOptions: Partial<AIChatWidgetOptions> = {
  botName: "Support Agent",
  primaryColor: "#007bff",
  position: "right",
  initialMessages: [],
};

/**
 * Bundle name constant for the AI Chat Widget
 * Used to identify the widget script tag in auto-initialization
 * This allows the detection logic to work with versioned/hashed bundle names
 * (e.g., ai-chat-widget.umd.js, ai-chat-widget-v1.0.0.umd.js, ai-chat-widget-abc123.umd.js)
 */
const BUNDLE_NAME = "ai-chat-widget";

/**
 * Pattern to match widget bundle script tags
 * Matches variations like:
 * - ai-chat-widget.umd.js
 * - ai-chat-widget-v1.0.0.umd.js
 * - ai-chat-widget-abc123.umd.js
 * - ai-chat-widget.min.js
 *
 * Uses a literal regex pattern (not dynamically constructed) to avoid ReDoS security concerns.
 * The pattern matches BUNDLE_NAME followed by optional version/hash suffix and .js extension.
 */
const BUNDLE_PATTERN = /ai-chat-widget([.-].*)?\.js$/;

// Add cookie helper function
const getCookie = (name: string) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(";").shift();
  return null;
};

const ALLOWED_API_BASE_URLS = [
  "https://api.askinfosec.tech",
  "https://dev.api.askinfosec.tech",
  "http://localhost:5005",
  "http://127.0.0.1:5005",
] as const;

type AllowedApiBaseUrl = (typeof ALLOWED_API_BASE_URLS)[number];

const resolveAllowedApiBaseUrl = (apiBaseUrl: string): AllowedApiBaseUrl => {
  const normalizedApiBaseUrl = apiBaseUrl.replace(/\/+$/, "");
  const allowedMatch = ALLOWED_API_BASE_URLS.find((allowed) => allowed === normalizedApiBaseUrl);
  if (!allowedMatch)
    throw new Error("Invalid apiBaseUrl. Only approved API base URLs are allowed.");
  return allowedMatch;
};

const buildWidgetApiUrl = (apiBaseUrl: AllowedApiBaseUrl, appId: string, path: string) => {
  const encodedOrganizationId = encodeURIComponent(appId);
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  // Note: apiBaseUrl is a strict allowlist union type.
  return `${apiBaseUrl}/v1/organizations/${encodedOrganizationId}/ai-widget${normalizedPath}`;
};

const fetchFromAllowedApiBaseUrl = (
  allowedApiBaseUrl: AllowedApiBaseUrl,
  appId: string,
  path: string,
  init: RequestInit,
) => {
  const encodedOrganizationId = encodeURIComponent(appId);
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;

  // Use a switch with literal bases so static analyzers can see the host is not user-controlled.
  switch (allowedApiBaseUrl) {
    case "https://api.askinfosec.tech":
      return fetch(
        `https://api.askinfosec.tech/v1/organizations/${encodedOrganizationId}/ai-widget${normalizedPath}`,
        init,
      );
    case "https://dev.api.askinfosec.tech":
      return fetch(
        `https://dev.api.askinfosec.tech/v1/organizations/${encodedOrganizationId}/ai-widget${normalizedPath}`,
        init,
      );
    case "http://localhost:5005":
      return fetch(
        `http://localhost:5005/v1/organizations/${encodedOrganizationId}/ai-widget${normalizedPath}`,
        init,
      );
    case "http://127.0.0.1:5005":
      return fetch(
        `http://127.0.0.1:5005/v1/organizations/${encodedOrganizationId}/ai-widget${normalizedPath}`,
        init,
      );
  }
};

const validateExistingToken = async (
  existingToken: string,
  baseUrl: string,
  appId: string,
): Promise<boolean> => {
  try {
    const allowedBaseUrl = resolveAllowedApiBaseUrl(baseUrl);
    const validationResponse = await fetchFromAllowedApiBaseUrl(
      allowedBaseUrl,
      appId,
      "/validate-token",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-App-Id": appId,
        },
        body: JSON.stringify({ token: existingToken }),
      },
    );

    const validationData = await validationResponse.json();
    return validationResponse.ok && validationData.valid;
  } catch (validationError) {
    console.log("⚠️ Token validation failed:", validationError);
    return false;
  }
};

const fetchAndSetNewToken = async (baseUrl: string, appId: string): Promise<boolean> => {
  try {
    const allowedBaseUrl = resolveAllowedApiBaseUrl(baseUrl);
    const response = await fetchFromAllowedApiBaseUrl(allowedBaseUrl, appId, "/token", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-App-Id": appId,
      },
    });

    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

    const data = await response.json();

    if (data.error) {
      console.error("Error getting token", data.error);
      return false;
    }

    if (data.token) {
      const cookieSettings = [
        `aiagent-token=${data.token}`,
        "path=/",
        "max-age=86400",
        "SameSite=Lax",
        process.env.NODE_ENV === "production" ? "Secure" : "",
      ].join("; ");

      document.cookie = cookieSettings;
      return true;
    }

    return false;
  } catch (error) {
    console.error("Error fetching new token:", error);
    return false;
  }
};

const fetchWidgetOptions = async (
  apiBaseUrl: string,
  appId: string,
): Promise<AIChatWidgetOptions | null> => {
  const allowedBaseUrl = resolveAllowedApiBaseUrl(apiBaseUrl);
  const widgetOptionsApiUrl = buildWidgetApiUrl(allowedBaseUrl, appId, "/widget-options");
  const existingToken = getCookie("aiagent-token");
  console.log("🔍 Fetching widget options from:", widgetOptionsApiUrl);

  try {
    const response = await fetchFromAllowedApiBaseUrl(allowedBaseUrl, appId, "/widget-options", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-App-Id": appId,
        ...(existingToken ? { Authorization: `Bearer ${existingToken}` } : {}),
      },
    });

    if (!response.ok) {
      console.error("Error fetching widget options:", response.statusText);
      return null;
    }

    const responseData = await response.json();
    console.log("📊 Widget options API response:", responseData);

    // Extract data from response, handling different possible structures
    let apiData = null;
    if (responseData.status === 200 && responseData.data) {
      apiData = responseData.data;
    } else if (responseData.data) {
      apiData = responseData.data;
    } else if (!responseData.error) {
      // If there's no error field, the response itself might be the data
      apiData = responseData;
    }

    if (apiData) {
      const enabled = typeof apiData.enabled === "boolean" ? apiData.enabled : true;
      if (!enabled) {
        console.log("🚫 Widget is disabled by organization settings. Skipping init.");
        return { enabled: false };
      }

      // Get initialMessages - handle different possible structures
      let initialMessagesFromAPI = [];

      if (Array.isArray(apiData.initialMessages)) {
        initialMessagesFromAPI = apiData.initialMessages;
      } else if (apiData.initialQuestions && Array.isArray(apiData.initialQuestions)) {
        // API might use 'initialQuestions' instead of 'initialMessages'
        initialMessagesFromAPI = apiData.initialQuestions;
      } else if (apiData.suggestedQuestions && Array.isArray(apiData.suggestedQuestions)) {
        // Or might use 'suggestedQuestions'
        initialMessagesFromAPI = apiData.suggestedQuestions;
      }

      console.log("💬 Initial messages from API:", initialMessagesFromAPI);

      // Conditionally build widgetOptions
      const widgetOptions: AIChatWidgetOptions = {
        enabled,
        botName: (apiData.assistantName || apiData.botName || defaultOptions.botName) as string,
        primaryColor: (apiData.primaryColor || defaultOptions.primaryColor) as string,
        secondaryColor: (apiData.secondaryColor || defaultOptions.secondaryColor) as string,
        position: (apiData.position || defaultOptions.position) as "left" | "right",
        initialMessages:
          initialMessagesFromAPI.length > 0
            ? initialMessagesFromAPI
            : defaultOptions.initialMessages,
      };

      // Add botLogo only if it exists and is a string
      if (apiData.botLogo && typeof apiData.botLogo === "string") {
        const raw = apiData.botLogo as string;
        widgetOptions.botLogo = raw.startsWith("/") ? `${allowedBaseUrl}${raw}` : raw;
      }

      return widgetOptions;
    }

    if (responseData.error) {
      console.error("Error fetching widget options:", responseData.error);
    } else {
      console.error("Unexpected response format from widget options API");
    }

    return null;
  } catch (error) {
    console.error("Exception fetching widget options:", error);
    return null;
  }
};

const createWidget = async (
  apiBaseUrl: string,
  validatedOptions: AIChatWidgetOptionsWithAppIdAndToken,
  prefetchedWidgetOptions?: AIChatWidgetOptions | null,
) => {
  try {
    // Clean up any existing widget
    const oldContainer = document.getElementById("ai-chat-widget-container");
    if (oldContainer) {
      oldContainer.remove();
    }

    // Create new container
    const widgetContainer = document.createElement("div");
    widgetContainer.id = "ai-chat-widget-container";

    // Apply position styling to the container
    widgetContainer.style.position = "fixed";
    widgetContainer.style.zIndex = "10000"; // Increase z-index for better stacking
    widgetContainer.style.bottom = "0";
    widgetContainer.style[validatedOptions.position || "right"] = "0";
    widgetContainer.style.padding = "20px"; // Add padding to contain the chat button
    widgetContainer.style.boxSizing = "border-box";
    widgetContainer.style.pointerEvents = "none"; // Let clicks pass through container except for children

    // Check if Shadow DOM is supported
    const useShadowDom = isShadowDomSupported();
    let rootElement: HTMLElement;

    if (useShadowDom) {
      // Create Shadow DOM root for style isolation
      rootElement = createShadowRoot(widgetContainer);
      console.log("📍 Created widget container with Shadow DOM");
    } else {
      // Fallback for browsers that don't support Shadow DOM
      rootElement = widgetContainer;
      console.log("📍 Created widget container without Shadow DOM (not supported)");
    }

    // Add the container to the page
    document.body.appendChild(widgetContainer);

    // Create a React root and render the chat widget
    const root = createRoot(rootElement);

    const widgetOptions =
      prefetchedWidgetOptions === undefined
        ? await fetchWidgetOptions(apiBaseUrl, validatedOptions.appId)
        : prefetchedWidgetOptions;

    if (widgetOptions?.enabled === false) {
      // Clean up container so we don't leave UI artifacts when disabled
      const container = document.getElementById("ai-chat-widget-container");
      if (container) container.remove();
      return;
    }

    // Merge API options with passed-in options
    console.log("🔄 Creating final widget options...");
    const finalWidgetOptions: AIChatWidgetProps = {
      botName: widgetOptions?.botName ?? defaultOptions.botName ?? "Support Agent",
      primaryColor: widgetOptions?.primaryColor ?? defaultOptions.primaryColor ?? "#007bff",
      secondaryColor: widgetOptions?.secondaryColor ?? defaultOptions.secondaryColor ?? "#007bff",
      position: widgetOptions?.position ?? defaultOptions.position ?? "right",
      initialMessages: widgetOptions?.initialMessages ?? defaultOptions.initialMessages ?? [],
      appId: validatedOptions.appId,
      token: validatedOptions.token,
      apiBaseUrl: apiBaseUrl,
      useShadowDom: useShadowDom,
    };

    // Add botLogo only if it exists
    if (widgetOptions?.botLogo) {
      finalWidgetOptions.botLogo = widgetOptions.botLogo;
    }

    // Log the final options
    console.log("✅ Final widget options:", {
      botName: finalWidgetOptions.botName,
      hasLogo: !!finalWidgetOptions.botLogo,
      primaryColor: finalWidgetOptions.primaryColor,
      secondaryColor: finalWidgetOptions.secondaryColor,
      position: finalWidgetOptions.position,
      initialMessagesCount: finalWidgetOptions.initialMessages?.length,
      appId: finalWidgetOptions.appId,
      hasToken: !!finalWidgetOptions.token,
      apiBaseUrl: finalWidgetOptions.apiBaseUrl,
      useShadowDom,
    });

    // Render the chat widget with ErrorBoundary
    root.render(React.createElement(WrappedAIChatWidget, finalWidgetOptions));
    console.log("🎨 Widget rendered successfully with error boundary");
  } catch (error) {
    console.error("Error creating widget:", error);
  }
};

// Update the initWhenReady function
const initWhenReady = (
  apiBaseUrl: string,
  validatedOptions: AIChatWidgetOptionsWithAppIdAndToken,
  prefetchedWidgetOptions?: AIChatWidgetOptions | null,
) => {
  return new Promise<void>((resolve) => {
    const attemptCreation = () => {
      if (document.body) {
        createWidget(apiBaseUrl, validatedOptions, prefetchedWidgetOptions);
        resolve();
      } else {
        requestAnimationFrame(attemptCreation);
      }
    };

    if (document.readyState === "complete") {
      attemptCreation();
    } else {
      window.addEventListener("load", attemptCreation);
    }
  });
};

/**
 * Handles the token validation and initialization flow
 * 1. Validates existing token if present
 * 2. Fetches new token if needed
 * 3. Initializes the widget
 */
const handleTokenAndInitialization = async (
  apiBaseUrl: string,
  validatedOptions: AIChatWidgetOptionsWithAppIdAndToken,
): Promise<void | null> => {
  try {
    const validatedApiBaseUrl = resolveAllowedApiBaseUrl(apiBaseUrl);

    // If the widget is disabled, do not generate/validate tokens and do not render.
    const prefetchedWidgetOptions = await fetchWidgetOptions(
      validatedApiBaseUrl,
      validatedOptions.appId,
    );
    if (prefetchedWidgetOptions?.enabled === false) return null;

    // Step 1: Check for existing valid token
    const existingToken = getCookie("aiagent-token");
    if (existingToken) {
      const isExistingTokenValid = await validateExistingToken(
        existingToken,
        validatedApiBaseUrl,
        validatedOptions.appId,
      );

      if (isExistingTokenValid) {
        validatedOptions.token = existingToken;
        // Step 3a: Initialize widget with existing valid token
        return initWhenReady(validatedApiBaseUrl, validatedOptions, prefetchedWidgetOptions);
      }
    }

    // Step 2: Get new token if no valid token exists
    const tokenSet = await fetchAndSetNewToken(validatedApiBaseUrl, validatedOptions.appId);
    if (!tokenSet) {
      return null;
    }

    const newToken = getCookie("aiagent-token");
    if (newToken) validatedOptions.token = newToken;

    // Step 3b: Initialize widget with new token
    return initWhenReady(validatedApiBaseUrl, validatedOptions, prefetchedWidgetOptions);
  } catch (error) {
    console.error("Initialization error:", error);
    return null;
  }
};

/**
 * Initializes and mounts the AI Chat Widget into the webpage
 * This function is exposed globally and can be called from any webpage
 *
 * @param options - Configuration options for the chat widget
 * @example
 * window.AIChatWidget.initAIChatWidget({
 *   botName: 'Support Bot',
 *   primaryColor: '#007bff',
 *   position: 'right',
 *   appId: 'your-app-id'
 * });
 */
export async function initAIChatWidget(
  options: Partial<AIChatWidgetOptionsWithAppIdAndToken> = {},
) {
  const validatedOptions = {
    ...defaultOptions,
    ...options,
  };

  if (!validatedOptions.appId) {
    throw new Error("appId is required");
  }

  // Extract token from script URL if not provided in options
  if (!validatedOptions.token) {
    const scripts = document.getElementsByTagName("script");
    const currentScript = scripts.length > 0 ? scripts[scripts.length - 1] : null;
    if (currentScript) {
      const urlParams = new URLSearchParams(currentScript.src.split("?")[1]);
      validatedOptions.token = urlParams.get("token") || "";
    } else {
      console.warn("Could not find the script tag to extract token.");
      validatedOptions.token = "";
    }
  }

  // Add domain validation
  // TODO: We need to validate the currentDomain against the registered domains of the client/customer
  // What we can do is that before we call initAIChatWidget() or before completing initaliztion, we can call an api endpoint passing appId and with referer information, the backend api can validate if that will match.
  // const currentDomain = new URL(window.location.href).hostname;
  // console.log(
  //   `initAIChatWidget() currentDomain: ${currentDomain}, options.domain: ${options.domain}`
  // );

  // Resolve API base URL in a robust way
  // Priority: explicit option > script tag param > global config > env var
  const scripts = document.getElementsByTagName("script");
  const currentScript = scripts.length > 0 ? scripts[scripts.length - 1] : null;
  const scriptUrlParams = currentScript
    ? new URLSearchParams(currentScript.src.split("?")[1] || "")
    : null;
  const scriptApiBaseUrl = scriptUrlParams?.get("apiBaseUrl") || undefined;
  const globalApiBaseUrl = (window.AIChatWidgetConfig as AIChatWidgetConfig | undefined)
    ?.apiBaseUrl;
  const resolvedBaseUrl = (
    validatedOptions.apiBaseUrl ||
    scriptApiBaseUrl ||
    globalApiBaseUrl ||
    ""
  ).replace(/\/+$/, "");

  if (!resolvedBaseUrl) {
    console.error(
      "AIChatWidget: Missing apiBaseUrl. Provide via options.apiBaseUrl, script ?apiBaseUrl=, window.AIChatWidgetConfig.apiBaseUrl, or VITE_API_BASE_URL.",
    );
    return null;
  }
  try {
    resolveAllowedApiBaseUrl(resolvedBaseUrl);
  } catch (error) {
    console.error(
      "AIChatWidget: Invalid apiBaseUrl. Only *.askinfosec.tech and localhost/127.0.0.1 are allowed.",
      error,
    );
    return null;
  }
  // Start the initialization flow
  return handleTokenAndInitialization(
    resolvedBaseUrl,
    validatedOptions as AIChatWidgetOptionsWithAppIdAndToken,
  );
}

/**
 * Global type declaration for the window object
 * Ensures TypeScript recognizes the AIChatWidget global variable
 */
declare global {
  interface Window {
    AIChatWidget: {
      initAIChatWidget: typeof initAIChatWidget;
    };
    AIChatWidgetConfig?: AIChatWidgetConfig;
    __AIChatWidgetInitialized?: boolean;
  }
}

/**
 * Expose the initialization function globally
 * This makes it accessible via window.AIChatWidget.initAIChatWidget
 */
window.AIChatWidget = {
  initAIChatWidget,
};

/**
 * Dispatch a custom event when the widget is ready
 * This allows websites to listen for widget initialization
 * @example
 * document.addEventListener('AIChatWidgetReady', () => {
 *   // Initialize widget here
 * });
 */
document.dispatchEvent(new Event("AIChatWidgetReady"));

// --- AUTO-INITIALIZATION LOGIC (SEAMLESS INTEGRATION) ---

(function autoInitAIChatWidget() {
  // Skip if running in a non-browser environment
  if (typeof window === "undefined") return;

  // Prevent double-initialization if already called manually
  if (window.__AIChatWidgetInitialized) return;

  // Helper to extract config from script tag URL
  function getConfigFromScriptTag(): Partial<AIChatWidgetOptionsWithAppIdAndToken> {
    const scripts = document.getElementsByTagName("script");
    for (let i = scripts.length - 1; i >= 0; i--) {
      const script = scripts[i];
      // Match widget bundle using flexible pattern (handles versioning/hashing)
      if (script?.src && BUNDLE_PATTERN.test(script.src)) {
        const urlParams = new URLSearchParams(script.src.split("?")[1] || "");
        const config: Partial<AIChatWidgetOptionsWithAppIdAndToken> = {};

        // Handle appId specially
        const appId = urlParams.get("appId");
        if (appId) config.appId = appId;

        // Handle token
        const token = urlParams.get("token");
        if (token) config.token = token;

        // Handle other options
        const botName = urlParams.get("botName");
        if (botName) config.botName = botName;

        const primaryColor = urlParams.get("primaryColor");
        if (primaryColor) config.primaryColor = primaryColor;

        const secondaryColor = urlParams.get("secondaryColor");
        if (secondaryColor) config.secondaryColor = secondaryColor;

        const position = urlParams.get("position");
        if (position && (position === "left" || position === "right")) {
          config.position = position;
        }

        return config;
      }
    }
    return {};
  }

  // Run auto-initialization on DOMContentLoaded or immediately if already loaded
  const initializeWidget = () => {
    try {
      // Merge config: script tag params > global config > {}
      const globalConfig = window.AIChatWidgetConfig || {};
      const scriptConfig = getConfigFromScriptTag();

      // Script params take priority over global config
      const mergedConfig: Partial<AIChatWidgetOptionsWithAppIdAndToken> = {
        ...globalConfig,
        ...scriptConfig,
      };

      // Respect autoInit flag (default true if not specified)
      if (globalConfig.autoInit === false) return;

      // Require appId for auto-init
      if (!mergedConfig.appId) {
        // Only log if there was an attempt to configure
        if (Object.keys(globalConfig).length > 0 || Object.keys(scriptConfig).length > 0) {
          console.warn("AIChatWidget: Auto-initialization skipped - appId is required");
        }
        return;
      }

      // Mark as initialized to prevent double init
      window.__AIChatWidgetInitialized = true;

      // Call the existing initialization function
      if (window.AIChatWidget && typeof window.AIChatWidget.initAIChatWidget === "function") {
        window.AIChatWidget.initAIChatWidget(mergedConfig);
      }
    } catch (error) {
      console.error("AIChatWidget: Auto-initialization failed", error);
    }
  };

  // Execute when DOM is ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initializeWidget);
  } else {
    initializeWidget();
  }
})();
